<template>
  <div class="container profile-detail-block event-block">
    <div
      class="event-card mb-2"
      :class="{ clickable: clickable }"
      @click="handleClick"
    >
      <div class="event-info p-10">
        <div class="d-flex event-card-content">
          <!-- Purple Date Box -->
          <div class="event-date-box">
            <div
              class="event-date-text"
              v-if="useMemoize"
              v-memoize:formatEventDateShort="[event.event_date.from]"
            ></div>
            <div class="event-date-text" v-else>
              {{ formatEventDateShort(event.event_date.from) }}
            </div>
            <div
              class="event-time-text"
              v-if="useMemoize"
              v-memoize:formatEventTime="[event.event_date.from]"
            ></div>
            <div class="event-time-text" v-else>
              {{ formatEventTime(event.event_date.from) }}
            </div>
          </div>

          <!-- Event Content (Title and Companies) -->
          <div class="event-content-wrapper flex-grow-1">
            <!-- Desktop: Event name first, then companies -->
            <p class="event-name event-name-desktop">{{ event.event_name }}</p>
            <div class="event-companies">
              <span
                v-for="(company, idx) in event.companies"
                :key="company._id"
              >
                {{ company.name
                }}<span v-if="idx < event.companies.length - 1">, </span>
              </span>
            </div>
            <!-- Mobile: Date and Companies on same line wrapper -->
            <div class="event-header-mobile">
              <div class="event-companies-mobile">
                <span
                  v-for="(company, idx) in event.companies"
                  :key="company._id"
                  style="white-space: inherit;"
                >
                  {{ company.name }}<span v-if="idx < event.companies.length - 1">, </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Event Name - Independent row in mobile -->
          <p class="event-name event-name-mobile">
            {{ event.event_name }}
          </p>
          <!-- Clock Icon and Status -->
          <div class="event-status-wrapper">
            <div class="event-deadline-info">
              <i
                class="fa fa-clock-o event-clock-icon-red"
                aria-hidden="true"
              ></i>
              <span
                class="event-deadline-date"
                v-if="useMemoize"
                v-memoize:formatApplicationDeadline="[
                  event.application_period.to,
                ]"
              >
              </span>
              <span class="event-deadline-date" v-else>{{
                formatApplicationDeadline(event.application_period.to)
              }}</span>
            </div>
            <button
              :class="getApplicationButtonClass(event)"
              class="btn application-status-btn"
              @click.stop="handleButtonClick"
            >
              {{ getApplicationButtonText(event) }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import eventHelpers from '../mixins/eventHelpers';

export default {
  name: 'EventCard',
  mixins: [eventHelpers],
  props: {
    event: {
      type: Object,
      required: true,
    },
    clickable: {
      type: Boolean,
      default: true,
    },
    useMemoize: {
      type: Boolean,
      default: false,
    },
    showMobileLayout: {
      type: Boolean,
      default: true,
    },
  },
  directives: {
    memoize: {
      bind(el, binding, vnode) {
        const cache = new Map();
        const key = JSON.stringify(binding.value);
        if (!cache.has(key)) {
          cache.set(key, vnode.context[binding.arg](...binding.value));
        }
        el.textContent = cache.get(key);
      },
    },
  },
  computed: {
    ...mapGetters({
      profile: 'getProfile',
    }),
  },
  methods: {
    handleClick() {
      if (this.clickable && this.$listeners.click) {
        this.$emit('click', this.event._id);
      }
    },
    handleButtonClick() {
      if (this.$listeners.buttonClick) {
        this.$emit('buttonClick', this.event);
      } else if (this.clickable) {
        this.handleClick();
      }
    },
  },
};
</script>

<style scoped>
.event-block {
  position: relative;
  padding: 10px 25px !important;
  margin-bottom: 15px;
  border-radius: 10px;
  box-shadow: 4px 4px 4px 4px #0000000d;
  background: #ffffff !important;
}

.event-card {
  border: 0px;
}

.event-card.clickable {
  cursor: pointer;
}

.event-card-content {
  align-items: flex-start;
  gap: 15px;
}

.event-date-box {
  background-color: #e0e7ff;
  padding: 10px 15px;
  border-radius: 10px;
  min-width: 70px;
  height: 70px;
  text-align: center;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.event-date-text {
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  margin-bottom: 5px;
}

.event-time-text {
  font-size: 16px;
  font-weight: 400;
  color: #000000;
}

.event-content-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.event-name {
  font-weight: 800;
  font-size: 18px;
  margin-bottom: 8px;
  line-height: 1.4;
  color: #000000;
  margin-top: 0;
}

.event-companies {
  font-size: 14px;
  color: #000000;
  margin-top: 12px;
}

.event-status-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex-shrink: 0;
  min-width: 150px;
}

.event-deadline-info {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 8px;
  gap: 5px;
}

.event-clock-icon-red {
  font-size: 18px;
  color: #dd3847;
  flex-shrink: 0;
}

.event-deadline-date {
  font-size: 14px;
  font-weight: 400;
  color: #dd3847;
  white-space: nowrap;
}

.application-status-btn {
  border-radius: 20px;
  padding: 2px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  width: 168px;
  height: 25px;
}

.applied-status {
  background-color: #e5e7eb;
  color: #515c6b;
}

.not-applied-status {
  background-color: #e0e7ff;
  color: #483aca;
}

.p-10 {
  padding: 10px;
}

/* Hide mobile-specific elements on desktop */
.event-header-mobile {
  display: none;
}

.event-companies-mobile {
  display: none;
}

.event-name-mobile {
  display: none;
}

/* Mobile styles */
@media (max-width: 550px) {
  .event-block {
    padding: 10px !important;
  }

  .event-card-content {
    flex-wrap: wrap;
    gap: 0;
  }

  .event-date-box {
    width: auto;
    min-width: 60px;
    height: 60px;
    padding: 6px 8px;
    flex-shrink: 0;
    margin-right: 10px;
  }

  .event-date-text,
  .event-time-text {
    font-size: 11px;
    margin-bottom: 2px;
  }

  .event-content-wrapper {
    display: flex;
    flex-direction: column;
    width: calc(100% - 80px);
    min-width: 0;
    flex: 1;
  }

  .event-companies {
    display: none;
  }

  .event-name-desktop {
    display: none;
  }

  .event-header-mobile {
    display: flex;
    align-items: center;
    margin-bottom: 0;
    width: 100%;
    min-height: 60px;
  }

  .event-companies-mobile {
    display: inline;
    align-items: center;
    margin-top: 0;
    margin-left: 0;
    font-size: 12px;
    flex: 1;
    color: #000000;
  }

  .event-name-mobile {
    display: block;
    font-size: 16px;
    line-height: 1.4;
    margin-top: 10px;
    margin-bottom: 10px;
    width: 100%;
    padding-top: 10px;
    border-top: 1px solid #e0e0e0;
    font-weight: 800;
    color: #000000;
    word-break: break-word;
  }

  .event-status-wrapper {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-top: 0;
  }

  .event-deadline-info {
    margin-bottom: 0;
    margin-right: 10px;
  }

  .event-clock-icon-red {
    font-size: 16px;
  }

  .event-deadline-date {
    font-size: 12px;
  }

  .application-status-btn {
    font-size: 12px;
    min-width: 90px;
  }
}
</style>
